import { useCallback, useMemo, useState } from 'react'
import type { SelectedFile } from '../contexts/SelectedFilesContext'
import { SelectedFilesContext } from '../contexts/SelectedFilesContext'

interface SelectedFilesContextProviderProps {
  children: React.ReactNode
}

/**
 * SelectedFilesContextProvider manages selected files and sections for chat context.
 * Context can be manipulated from both Home page and Files page.
 * Persists selections across navigation.
 */
export const SelectedFilesContextProvider = ({ children }: SelectedFilesContextProviderProps) => {
  const [selectedFiles, setSelectedFiles] = useState<SelectedFile[]>([])

  const clearAll = useCallback(() => {
    setSelectedFiles([])
  }, [])

  const getFileSelection = useCallback(
    (fileId: string) => selectedFiles.find((file) => file.id === fileId),
    [selectedFiles]
  )

  /**
   * Upserts or removes a file from the selection.
   * - sections: []    → entire file selected
   * - sections: [...] → partial selection (minimal representation)
   * - sections: null  → remove file from context
   */
  const updateFileSelection = useCallback(({ sections, ...file }: SelectedFile) => {
    setSelectedFiles((prev) => {
      if (sections === null) return prev.filter((f) => f.id !== file.id)

      const existingIndex = prev.findIndex((f) => f.id === file.id)
      const updated = { ...file, sections }

      if (existingIndex >= 0) {
        const next = [...prev]
        next[existingIndex] = updated
        return next
      }

      return [...prev, updated]
    })
  }, [])

  const replaceSelections = useCallback((files: SelectedFile[]) => {
    setSelectedFiles(files.filter((f) => f.sections !== null))
  }, [])

  const value = useMemo(
    () => ({ selectedFiles, clearAll, updateFileSelection, getFileSelection, replaceSelections }),
    [selectedFiles, clearAll, updateFileSelection, getFileSelection, replaceSelections]
  )

  return <SelectedFilesContext value={value}>{children}</SelectedFilesContext>
}
